import React from 'react';
import { Team, Campaign } from '../types';
import { Send, AlertCircle, CheckCircle2, Loader2, Users, Megaphone } from 'lucide-react';
interface DistributionSummaryProps {
  teams: Team[];
  campaigns: Campaign[];
  selectedTeamIds: string[];
  teamCampaignMap: Record<string, string>;
  totalLeads: number;
  onSend: () => void;
  isSending: boolean;
  hasSent: boolean;
}
export function DistributionSummary({
  teams,
  campaigns,
  selectedTeamIds,
  teamCampaignMap,
  totalLeads,
  onSend,
  isSending,
  hasSent
}: DistributionSummaryProps) {
  const selectedTeams = teams.filter(t => selectedTeamIds.includes(t.id));
  const distribution = selectedTeams.map((team, idx) => {
    const base = Math.floor(totalLeads / selectedTeams.length);
    const remainder = totalLeads % selectedTeams.length;
    const count = base + (idx < remainder ? 1 : 0);
    const campaign = campaigns.find(c => c.id === teamCampaignMap[team.id]) || null;
    return {
      team,
      count,
      campaign
    };
  });
  const maxCount = Math.max(...distribution.map(d => d.count), 1);
  const allTeamsHaveCampaigns = selectedTeams.length > 0 && selectedTeams.every(t => !!teamCampaignMap[t.id]);
  const canSend = selectedTeams.length > 0 && totalLeads > 0 && allTeamsHaveCampaigns;
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full" data-id="element-14">
      <div className="px-6 py-5 border-b border-slate-200 bg-slate-50/50" data-id="element-15">
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-16">
          3. Review & Send to Bonzo
        </h2>
        <p className="text-sm text-slate-500 mt-0.5" data-id="element-17">
          Round-robin split per team into their selected campaign.
        </p>
      </div>

      <div className="p-6 flex-grow flex flex-col" data-id="element-18">
        {selectedTeamIds.length === 0 ? <div className="flex-grow flex flex-col items-center justify-center text-center p-6 bg-slate-50 rounded-lg border border-dashed border-slate-300" data-id="element-19">
            <AlertCircle className="w-8 h-8 text-slate-400 mb-3" data-id="element-20" />
            <p className="text-slate-600 font-medium" data-id="element-21">No teams selected</p>
            <p className="text-slate-500 text-sm mt-1" data-id="element-22">
              Select at least one team to preview distribution.
            </p>
          </div> : totalLeads === 0 ? <div className="flex-grow flex flex-col items-center justify-center text-center p-6 bg-slate-50 rounded-lg border border-dashed border-slate-300" data-id="element-23">
            <AlertCircle className="w-8 h-8 text-slate-400 mb-3" data-id="element-24" />
            <p className="text-slate-600 font-medium" data-id="element-25">No leads to distribute</p>
            <p className="text-slate-500 text-sm mt-1" data-id="element-26">
              Adjust filters to include leads.
            </p>
          </div> : <div className="flex-grow flex flex-col" data-id="element-27">
            <div className="flex items-baseline justify-between mb-4 pb-4 border-b border-slate-100" data-id="element-28">
              <span className="text-sm font-medium text-slate-600" data-id="element-29">
                Distributing
              </span>
              <div className="text-right" data-id="element-30">
                <span className="text-2xl font-bold text-slate-900 tabular-nums" data-id="element-31">
                  {totalLeads.toLocaleString()}
                </span>
                <span className="text-sm text-slate-500 ml-1.5" data-id="element-32">
                  leads → {selectedTeams.length}{' '}
                  {selectedTeams.length === 1 ? 'team' : 'teams'}
                </span>
              </div>
            </div>

            <div className="space-y-3 mb-6 flex-grow" data-id="element-33">
              {distribution.map(({
            team,
            count,
            campaign
          }) => {
            const pct = count / totalLeads * 100;
            return <div key={team.id} className="p-3 rounded-lg border border-slate-100 bg-slate-50/50" data-id="element-34">
                    <div className="flex items-center justify-between mb-2" data-id="element-35">
                      <div className="flex items-center gap-2 min-w-0" data-id="element-36">
                        <div className={`p-1.5 rounded ${team.bgClass} ${team.colorClass} flex-shrink-0`} data-id="element-37">
                          <Users className="w-3.5 h-3.5" data-id="element-38" />
                        </div>
                        <span className="font-medium text-slate-800 text-sm truncate" data-id="element-39">
                          {team.name}
                        </span>
                      </div>
                      <div className="text-right flex-shrink-0" data-id="element-40">
                        <span className="text-base font-bold text-slate-900 tabular-nums" data-id="element-41">
                          {count.toLocaleString()}
                        </span>
                        <span className="text-xs text-slate-500 ml-1" data-id="element-42">
                          ({pct.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-white rounded-full overflow-hidden mb-2" data-id="element-43">
                      <div className={`h-full rounded-full transition-all ${team.bgClass.replace('100', '500')}`} style={{
                  width: `${count / maxCount * 100}%`
                }} data-id="element-44" />
                    </div>
                    <div className="flex items-center gap-1.5 text-xs" data-id="element-45">
                      <Megaphone className="w-3 h-3 text-slate-400 flex-shrink-0" data-id="element-46" />
                      {campaign ? <span className="text-slate-600 truncate" data-id="element-47">
                          {campaign.name}
                        </span> : <span className="text-amber-600 font-medium" data-id="element-48">
                          No campaign selected
                        </span>}
                    </div>
                  </div>;
          })}
            </div>

            {hasSent ? <div className="w-full py-3 px-4 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-200 flex items-center justify-center gap-2 font-medium" data-id="element-49">
                <CheckCircle2 className="w-5 h-5" data-id="element-50" />
                Successfully sent to Bonzo!
              </div> : <>
                <button onClick={onSend} disabled={isSending || !canSend} className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors shadow-sm" data-id="element-51">
                  {isSending ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-52" />
                      Syncing with Bonzo...
                    </> : <>
                      <Send className="w-5 h-5" data-id="element-53" />
                      Send {totalLeads.toLocaleString()} Leads to Bonzo
                    </>}
                </button>
                {!allTeamsHaveCampaigns && <p className="text-xs text-amber-700 text-center mt-2" data-id="element-54">
                    Assign a Bonzo campaign to every selected team to send.
                  </p>}
              </>}
          </div>}
      </div>
    </div>;
}